import { Controller } from '@hotwired/stimulus'

// Manages the interact pane — a unified right-sidebar (desktop) /
// bottom-sheet (mobile) component used across Training and ScriptureDig.
//
// Strip mode  (Training): always shows a 48px header strip on mobile.
//   Expand / collapse is toggled by adding / removing .interact-pane--open.
//
// Overlay mode (ScriptureDig): completely hidden until content is injected
//   into the #interact_pane_content turbo frame via Turbo Streams.
//   CSS :has() automatically shows / hides the pane based on frame content.
//   close() empties the frame, which triggers the CSS collapse.
//
// Values:
//   mode  String  – "strip" | "overlay"
//
// Targets:
//   content  – the #interact_pane_content turbo frame
//   addTab   – the "Add" tab span (present when toolbar input buttons are shown)
export default class extends Controller {
  static values = { mode: String }
  static targets = ['content', 'addTab']

  connect () {
    this._onFrameLoad = (event) => this.#handleFrameLoad(event)
    document.addEventListener('turbo:frame-load', this._onFrameLoad)

    this._onStreamRender = (event) => this.#handleStreamRender(event)
    document.addEventListener('turbo:before-stream-render', this._onStreamRender)

    this._onTouchStart = (e) => { this._touchStartY = e.touches[0].clientY }
    this._onTouchMove = (e) => this.#preventBodyScroll(e)
    this.element.addEventListener('touchstart', this._onTouchStart, { passive: true })
    this.element.addEventListener('touchmove', this._onTouchMove, { passive: false })

    if (window.innerWidth < 1024) return

    // turbo:load fires after both full page loads and Turbo Drive navigations,
    // once the browser has completed a layout pass on the new content. This is
    // more reliable than document.fonts.ready for Turbo apps: fonts.ready
    // resolves immediately on Turbo navigations (fonts already loaded from the
    // previous page), causing us to measure before layout has settled.
    this._onTurboLoad = () => this.#measureAndApply()
    document.addEventListener('turbo:load', this._onTurboLoad)

    // Re-measure if the viewport is resized (e.g. user un-docks the window).
    // Debounced to avoid thrashing during a drag resize.
    this._onResize = this.#debounce(() => this.#measureAndApply(), 100)
    window.addEventListener('resize', this._onResize)
  }

  disconnect () {
    document.removeEventListener('turbo:frame-load', this._onFrameLoad)
    document.removeEventListener('turbo:before-stream-render', this._onStreamRender)
    document.removeEventListener('turbo:load', this._onTurboLoad)
    window.removeEventListener('resize', this._onResize)
    this.element.removeEventListener('touchstart', this._onTouchStart)
    this.element.removeEventListener('touchmove', this._onTouchMove)
  }

  close (event) {
    if (event) event.stopPropagation()
    if (this.modeValue === 'overlay') {
      if (this.hasContentTarget) this.contentTarget.innerHTML = ''
    } else {
      this.element.classList.remove('interact-pane--open')
    }
  }

  expand (event) {
    if (event) event.stopPropagation()
    this.element.classList.remove('interact-pane--minimized')
  }

  toggleMinimized (event) {
    if (event) event.stopPropagation()
    this.element.classList.toggle('interact-pane--minimized')
  }

  open (event) {
    this.element.classList.add('interact-pane--open')
    if (event && event.currentTarget) {
      this.#activateTab(event.currentTarget)
    }
  }

  toggle (event) {
    if (event) event.stopPropagation()
    this.element.classList.toggle('interact-pane--open')
  }

  // ── Private ───────────────────────────────────────────────────────────────

  #activateTab (clickedTab) {
    this.element.querySelectorAll('.interact-pane__tab').forEach(tab => {
      tab.classList.remove('interact-pane__tab--active')
    })
    clickedTab.classList.add('interact-pane__tab--active')
  }

  #debounce (fn, delay) {
    let timer
    return (...args) => {
      clearTimeout(timer)
      timer = setTimeout(() => fn(...args), delay)
    }
  }

  // Prevent the fixed bottom-sheet from leaking touch events to the body.
  // Calls preventDefault() on touchmove unless the touched element has a
  // scrollable ancestor (within the pane) that can still move in the drag
  // direction — in which case native scroll is allowed to proceed.
  #preventBodyScroll (e) {
    const deltaY = e.touches[0].clientY - (this._touchStartY ?? e.touches[0].clientY)
    let el = e.target
    while (el && el !== this.element) {
      const { overflowY } = window.getComputedStyle(el)
      if (overflowY === 'scroll' || overflowY === 'auto') {
        const canScrollUp = el.scrollTop > 0
        const canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight
        if ((deltaY < 0 && canScrollDown) || (deltaY > 0 && canScrollUp)) return
        break
      }
      el = el.parentElement
    }
    e.preventDefault()
  }

  // turbo:frame-load bubbles, so this listener on document catches all frame
  // loads. When the interact_pane_content frame loads an input_type_form URL
  // (triggered by a toolbar button), open the pane and activate the "Add" tab.
  #handleFrameLoad (event) {
    if (event.target.id !== 'interact_pane_content') return
    const src = event.target.getAttribute('src') || ''
    if (!src.includes('input_type_form')) return
    this.open()
    if (this.hasAddTabTarget) {
      this.addTabTarget.classList.remove('hidden')
      this.#activateTab(this.addTabTarget)
    }
  }

  // When a turbo stream updates interact_pane_content (i.e. after an input
  // point or question is submitted), reset the pane to its default state:
  // hide the Add tab, activate the first normal tab, and close on mobile
  // unless the incoming content is marked as persistent (phase-restore panels).
  // Also remove minimized when a non-persistent update arrives so the pane
  // opens fully (e.g. tapping a tool placement from the peek strip).
  #handleStreamRender (event) {
    const stream = event.detail.newStream
    if (stream.getAttribute('target') !== 'interact_pane_content') return
    if (stream.getAttribute('action') !== 'update') return
    if (this.hasAddTabTarget) this.addTabTarget.classList.add('hidden')
    const defaultTab = this.element.querySelector('a.interact-pane__tab')
    if (defaultTab) this.#activateTab(defaultTab)
    const template = stream.querySelector('template')
    const isPersistent = template &&
      template.content.querySelector('[data-interact-pane-persistent]')
    if (!isPersistent) this.element.classList.remove('interact-pane--minimized')
    if (window.innerWidth < 1024 && !isPersistent) this.close()
    if (window.innerWidth < 1024 && isPersistent) {
      const isRestoringFromTool = this.hasContentTarget &&
        this.contentTarget.querySelector('[data-tool-placement]')
      if (isRestoringFromTool) {
        this.element.classList.add('interact-pane--minimized')
      } else {
        this.element.classList.remove('interact-pane--minimized')
      }
    }
  }

  // Measure the pane's distance from the top of the document and store it as
  // --pane-initial-offset so the CSS scroll-driven animation can grow the pane
  // height from (100svh - offset) to its maximum as the user scrolls up to the
  // sticky point.
  //
  // Also measure the actual rendered height of #page-toolbar-bar (fixed at the
  // bottom of the viewport) and store it as --toolbar-height on this element,
  // overriding the rough server-side estimate on <body> with the precise pixel
  // value so the pane bottom never overlaps the toolbar.
  #measureAndApply () {
    if (window.innerWidth < 1024) return

    // Temporarily scroll to the top so getBoundingClientRect().top equals the
    // true document-top offset of the pane. Using scrollY to compensate is
    // unreliable: turbo:load can fire while the page is already scrolled (e.g.
    // after a Turbo Stream response), inflating the offset and causing the
    // scroll-driven height animation to shrink the pane when scrolling up.
    const savedScroll = window.scrollY
    if (savedScroll > 0) window.scrollTo({ top: 0, behavior: 'instant' })
    const rect = this.element.getBoundingClientRect()
    const offset = Math.max(0, Math.round(rect.top))
    if (savedScroll > 0) window.scrollTo({ top: savedScroll, behavior: 'instant' })
    this.element.style.setProperty('--pane-initial-offset', `${offset}px`)

    const toolbar = document.getElementById('page-toolbar-bar')
    if (toolbar) {
      const toolbarHeight = Math.round(toolbar.getBoundingClientRect().height)
      this.element.style.setProperty('--toolbar-height', `${toolbarHeight}px`)
    }
  }
}
